import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Empty,
  Input,
  Modal,
  PageHeader,
  Table,
  useAuth,
  useCan,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import {
  DOCUMENTS_KEY,
  useCenters,
  useDocumentsHub,
  type Center,
  type Room,
} from '../../lib/documents'

type CenterForm = { id?: string; name: string; city: string; address: string }
type RoomForm = { centerId: string; id?: string; name: string; capacity: string; position: string }

/** Centres d'examen et salles d'un concours (capacité = plafond de la répartition). */
export function CentersPage() {
  const { competitionId = '' } = useParams()
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = useCan('centers.manage', { type: 'competition', id: competitionId })
  const hub = useDocumentsHub()
  const competition = hub.data?.find((c) => c.id === competitionId)
  const centers = useCenters(competitionId)
  const [center, setCenter] = useState<CenterForm | null>(null)
  const [room, setRoom] = useState<RoomForm | null>(null)
  const [removing, setRemoving] = useState<Room | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const invalidate = () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY })
  const onError = (error: unknown) => {
    setErrors(fieldErrors(error))
    toast.error(errorMessage(error))
  }
  const saveCenter = useMutation({
    mutationFn: (f: CenterForm) => {
      const body = {
        name: f.name.trim(),
        city: f.city.trim() || null,
        address: f.address.trim() || null,
      }
      return f.id
        ? session.api.patch(`/admin/centers/${f.id}`, body)
        : session.api.post(`/admin/competitions/${competitionId}/centers`, body)
    },
    onSuccess: async () => {
      toast.success('Centre enregistré')
      setCenter(null)
      await invalidate()
    },
    onError,
  })
  const saveRoom = useMutation({
    mutationFn: (f: RoomForm) => {
      const body = {
        name: f.name.trim(),
        capacity: Number(f.capacity),
        position: f.position ? Number(f.position) : 0,
      }
      return f.id
        ? session.api.put(`/admin/rooms/${f.id}`, body)
        : session.api.post(`/admin/centers/${f.centerId}/rooms`, body)
    },
    onSuccess: async () => {
      toast.success('Salle enregistrée')
      setRoom(null)
      await invalidate()
    },
    onError,
  })
  const removeRoom = useMutation({
    mutationFn: (id: string) => session.api.delete(`/admin/rooms/${id}`),
    onSuccess: async () => {
      toast.success('Salle supprimée')
      setRemoving(null)
      await invalidate()
    },
    onError: (error) => {
      setRemoving(null)
      toast.error(errorMessage(error))
    },
  })

  const roomColumns = (c: Center): Column<Room>[] => [
    { key: 'name', header: 'Salle', cell: (r) => r.name },
    { key: 'capacity', header: 'Capacité', numeric: true, cell: (r) => r.capacity },
    { key: 'used', header: 'Places attribuées', numeric: true, cell: (r) => r.used },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            align: 'end' as const,
            cell: (r: Room) => (
              <span className="row" style={{ gap: 'var(--space-1)', justifyContent: 'flex-end' }}>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Modifier la salle ${r.name}`}
                  onClick={() => {
                    setErrors({})
                    setRoom({
                      centerId: c.id,
                      id: r.id,
                      name: r.name,
                      capacity: String(r.capacity),
                      position: String(r.position),
                    })
                  }}
                >
                  <Pencil aria-hidden="true" size={16} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Supprimer la salle ${r.name}`}
                  disabled={r.used > 0}
                  title={r.used > 0 ? 'Salle utilisée par des convocations' : undefined}
                  onClick={() => setRemoving(r)}
                >
                  <Trash2 aria-hidden="true" size={16} />
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ]

  const submitCenter = (e: FormEvent) => {
    e.preventDefault()
    if (center) saveCenter.mutate(center)
  }
  const submitRoom = (e: FormEvent) => {
    e.preventDefault()
    if (room) saveRoom.mutate(room)
  }

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/documents" className="link">
            Convocations et documents
          </Link>
        }
        title="Centres et salles d’examen"
        description={
          competition ? (
            <>
              {competition.title} · <span className="mono">{competition.reference}</span>
            </>
          ) : (
            'La capacité des salles plafonne la répartition des candidats : aucun surnombre.'
          )
        }
        actions={
          canManage && (
            <Button
              variant="primary"
              onClick={() => {
                setErrors({})
                setCenter({ name: '', city: '', address: '' })
              }}
            >
              <Plus aria-hidden="true" size={16} />
              Ajouter un centre
            </Button>
          )
        }
      />
      {!canManage && (
        <Alert tone="info">
          Consultation seule : la gestion des centres relève de la permission « centres ».
        </Alert>
      )}
      {centers.isError && <Alert tone="danger">{errorMessage(centers.error)}</Alert>}
      {centers.isPending && (
        <Card>
          <CardBody>Chargement…</CardBody>
        </Card>
      )}
      {centers.data?.length === 0 && (
        <Card>
          <Empty
            icon={<Building2 aria-hidden="true" />}
            title="Aucun centre d’examen"
            description="Ajoutez un centre puis ses salles avec leur capacité."
          />
        </Card>
      )}
      <div className="stack" style={{ gap: 'var(--space-6)' }}>
        {centers.data?.map((c) => (
          <Card key={c.id}>
            <CardHeader
              title={
                <span className="stack" style={{ gap: 2 }}>
                  <span>{c.name}</span>
                  <span className="muted" style={{ fontSize: 'var(--text-sm)', fontWeight: 400 }}>
                    {[c.city, c.address].filter(Boolean).join(' — ') || 'Adresse non renseignée'} ·{' '}
                    {c.capacity} places
                  </span>
                </span>
              }
              actions={
                canManage && (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setErrors({})
                        setCenter({
                          id: c.id,
                          name: c.name,
                          city: c.city ?? '',
                          address: c.address ?? '',
                        })
                      }}
                    >
                      Modifier
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setErrors({})
                        setRoom({
                          centerId: c.id,
                          name: `Salle ${c.rooms.length + 1}`,
                          capacity: '',
                          position: String(c.rooms.length),
                        })
                      }}
                    >
                      <Plus aria-hidden="true" size={16} />
                      Salle
                    </Button>
                  </>
                )
              }
            />
            <Table
              columns={roomColumns(c)}
              rows={c.rooms}
              rowKey={(r) => r.id}
              caption={`Salles — ${c.name}`}
              empty={<Empty title="Aucune salle" description="Ajoutez au moins une salle." />}
            />
          </Card>
        ))}
      </div>

      <Modal
        open={center !== null}
        onClose={() => setCenter(null)}
        title={center?.id ? 'Modifier le centre' : 'Nouveau centre d’examen'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCenter(null)}>
              Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="center-form"
              loading={saveCenter.isPending}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        {center && (
          <form id="center-form" className="stack" onSubmit={submitCenter} noValidate>
            <Input
              label="Nom du centre"
              required
              value={center.name}
              error={errors.name}
              onChange={(e) => setCenter({ ...center, name: e.target.value })}
            />
            <Input
              label="Ville"
              value={center.city}
              error={errors.city}
              onChange={(e) => setCenter({ ...center, city: e.target.value })}
            />
            <Input
              label="Adresse"
              value={center.address}
              error={errors.address}
              onChange={(e) => setCenter({ ...center, address: e.target.value })}
            />
          </form>
        )}
      </Modal>

      <Modal
        open={room !== null}
        onClose={() => setRoom(null)}
        title={room?.id ? 'Modifier la salle' : 'Nouvelle salle'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRoom(null)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" form="room-form" loading={saveRoom.isPending}>
              Enregistrer
            </Button>
          </>
        }
      >
        {room && (
          <form id="room-form" className="stack" onSubmit={submitRoom} noValidate>
            <Input
              label="Nom de la salle"
              required
              value={room.name}
              error={errors.name}
              onChange={(e) => setRoom({ ...room, name: e.target.value })}
            />
            <Input
              label="Capacité (places)"
              required
              type="number"
              inputMode="numeric"
              min={1}
              max={10000}
              value={room.capacity}
              error={errors.capacity}
              hint="Ne peut pas descendre sous la plus haute place déjà attribuée."
              onChange={(e) => setRoom({ ...room, capacity: e.target.value })}
            />
            <Input
              label="Ordre de remplissage"
              type="number"
              inputMode="numeric"
              min={0}
              value={room.position}
              error={errors.position}
              hint="Les salles sont remplies dans cet ordre."
              onChange={(e) => setRoom({ ...room, position: e.target.value })}
            />
          </form>
        )}
      </Modal>

      <ConfirmModal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) removeRoom.mutate(removing.id)
        }}
        tone="danger"
        title="Supprimer la salle ?"
        message={removing ? `La salle « ${removing.name} » sera supprimée.` : undefined}
        confirmLabel="Supprimer"
      />
    </>
  )
}
